/**
 * Studies Timeline Chart Component
 *
 * Displays studies count over time
 */

import { Loader2 } from 'lucide-react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { Card, CardHeader, CardTitle, CardContent } from '@/presentation/components/ui/Card'
import { useStudiesTimeline } from '@/application/dashboard'

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    value: number
    payload: { date: string; count: number }
  }>
  label?: string
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    const formattedDate = new Date(data.date).toLocaleDateString('es-CO', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
    return (
      <div className="bg-[var(--background)] border border-[var(--border)] rounded-lg p-3 shadow-lg">
        <p className="font-medium text-[var(--foreground)]">{formattedDate}</p>
        <p className="text-sm text-[var(--primary)]">
          {data.count} estudios
        </p>
      </div>
    )
  }
  return null
}

interface StudiesTimelineChartProps {
  days?: number
}

export function StudiesTimelineChart({ days = 30 }: StudiesTimelineChartProps) {
  const { data, isLoading, isError } = useStudiesTimeline(days)

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Estudios - Últimos {days} días</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[250px]">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Estudios - Últimos {days} días</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[250px] text-[var(--foreground-muted)]">
            Error al cargar datos
          </div>
        </CardContent>
      </Card>
    )
  }

  // Format dates for display
  const chartData = data.timeline.map((item) => ({
    ...item,
    displayDate: new Date(item.date).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
    }),
  }))

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Estudios - Últimos {days} días</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[250px] text-[var(--foreground-muted)]">
            Sin datos disponibles
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorStudies" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="displayDate"
                tick={{ fontSize: 11, fill: 'var(--foreground-muted)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--border)' }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--foreground-muted)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--border)' }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#0ea5e9"
                strokeWidth={2}
                fill="url(#colorStudies)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
